import {type DefaultTreeAdapterTypes, parse} from 'parse5';

/**
 * A minimal, environment-independent view of parsed markup: enough for
 * `sanitize` to walk, and nothing that ties it to a particular DOM
 * implementation.
 */
export type ParsedText = {type: 'text'; value: string};
export type ParsedElement = {
  type: 'element';
  /** Upper-cased, e.g. `A`. */
  tagName: string;
  /** Attribute names are lower-cased. */
  attributes: Array<{name: string; value: string}>;
  children: Array<ParsedNode>;
};
export type ParsedNode = ParsedText | ParsedElement;

// Node type constants, spelled out because the `Node` interface they normally
// come from doesn't exist under Node.js.
const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

function fromDOM(nodes: ArrayLike<ChildNode>): Array<ParsedNode> {
  const parsed: Array<ParsedNode> = [];
  for (const node of Array.from(nodes)) {
    switch (node.nodeType) {
      case TEXT_NODE:
        parsed.push({type: 'text', value: node.nodeValue ?? ''});
        break;
      case ELEMENT_NODE: {
        const element = node as Element;
        parsed.push({
          type: 'element',
          tagName: element.tagName.toUpperCase(),
          attributes: Array.from(element.attributes).map(({name, value}) => ({
            name: name.toLowerCase(),
            value,
          })),
          children: fromDOM(element.childNodes),
        });
        break;
      }
      // Comments, CDATA sections, processing instructions and doctypes carry
      // no renderable content, so they're dropped.
    }
  }
  return parsed;
}

function fromParse5(
  nodes: Array<DefaultTreeAdapterTypes.ChildNode>,
): Array<ParsedNode> {
  const parsed: Array<ParsedNode> = [];
  for (const node of nodes) {
    // Checked before `nodeName`, which is a plain `string` on elements and so
    // narrows nothing on its own.
    if ('tagName' in node) {
      parsed.push({
        type: 'element',
        tagName: node.tagName.toUpperCase(),
        attributes: node.attrs.map(({name, value}) => ({
          name: name.toLowerCase(),
          value,
        })),
        children: fromParse5(node.childNodes),
      });
    } else if (node.nodeName === '#text') {
      parsed.push({type: 'text', value: node.value});
    }
  }
  return parsed;
}

function findBody(
  node: DefaultTreeAdapterTypes.ParentNode,
): DefaultTreeAdapterTypes.Element | null {
  for (const child of node.childNodes) {
    if (!('tagName' in child)) {
      continue;
    }
    if (child.tagName === 'body') {
      return child;
    }
    const body = findBody(child);
    if (body) {
      return body;
    }
  }
  return null;
}

/**
 * Parses `markup` as HTML and returns the contents of the resulting `<body>`.
 *
 * Malformed markup is never an error: the HTML parsing spec defines recovery
 * for unbalanced tags, stray end tags and void elements, so anything a user can
 * type parses into *something*. That matters because the callers below
 * `sanitize` hand us whatever a podcaster wrote.
 *
 * Browsers parse with the native `DOMParser`; Node — server-side rendering in
 * sb-renderer, and tests — parses with parse5. Both implement the same spec, so
 * the two environments agree on how any given input recovers. (An XML parser
 * like `@xmldom/xmldom`, which this used to use, cannot: it rejects unbalanced
 * tags outright rather than recovering, so SSR threw on markup that browsers
 * rendered fine.) parse5 is stubbed out of browser bundles in
 * `webpack.externals.js`, since it's far larger than the native parser is free.
 */
export default function parseHTMLBody(markup: string): Array<ParsedNode> {
  // Both parsers are given the identical source string so that neither can
  // drift into fragment-parsing rules, which differ from a document's.
  const source = `<!DOCTYPE html><html><body>${markup}</body></html>`;

  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(source, 'text/html');
    return fromDOM(doc.body.childNodes);
  }

  const body = findBody(parse(source));
  return body ? fromParse5(body.childNodes) : [];
}
